import SvgIcon, { type SvgIconProps } from '@mui/material/SvgIcon';
import { useTheme } from '@mui/material/styles';

/**
 * Notepaya's own mark (#1823) — three linked nodes, the co-creation loop.
 * The product was renamed from CoPaya (#2148); the identifiers around it still
 * say `copaya`, because those are the external service's contract.
 *
 * Drawn by Mika as two 24×24 variants (DevPaya repo): one for light
 * backgrounds with the brand fills, one for dark ones with hollow nodes. Both
 * are reproduced here rather than imported as assets, matching `HighBeamIcon`
 * — a header icon that arrives as a separate network request can render a beat
 * after the bar around it.
 *
 * Both variants draw the outline in `currentColor` instead of the source's
 * hard-coded white and black: beside the other header icons that lands on
 * exactly their tone — `action.active`, black at 0.54 — in either theme, and
 * it follows hover and disabled states, which a fixed grey cannot. The fills
 * stay Mika's colours; that is the point of having two variants. Neither
 * variant is monochrome: the accent node is orange on both, and on dark the
 * other two nodes are hollow rather than filled.
 *
 * Shape order follows the source files: nodes first, connectors over them.
 * Reversing it tucks the connector ends behind the nodes, which is a
 * different drawing than the one that was handed over.
 */
export function NotepayaIcon(props: SvgIconProps) {
  const theme = useTheme();
  const onDark = theme.palette.mode === 'dark';

  // design-allow-hex: another product's mark. Substituting OS Buddy's theme
  // tokens here would redraw Notepaya's logo in the wrong colours, which is the
  // one thing an imported brand asset must not do.
  // The dark mark is not monochrome either: the accent node keeps the brand
  // orange, and the other two are left unfilled so the dark surface reads
  // through them.
  const nodeFill = onDark
    ? ['none', 'none', '#FF7A45'] // design-allow-hex: Notepaya brand accent
    : ['#FFEBDC', '#C2A38C', '#FF7A45']; // design-allow-hex: Notepaya brand fills
  const line = 'currentColor';

  return (
    <SvgIcon {...props} viewBox="0 0 24 24" fill="none">
      <circle cx="15" cy="19" r="4" fill={nodeFill[0]} stroke={line} strokeWidth="2" />
      <circle cx="5" cy="9" r="4" fill={nodeFill[1]} stroke={line} strokeWidth="2" />
      <circle cx="19" cy="5" r="4" fill={nodeFill[2]} stroke={line} strokeWidth="2" />
      <line x1="7.70711" y1="12.2929" x2="11.7071" y2="16.2929" stroke={line} strokeWidth="2" />
      <line x1="9.62861" y1="8.07152" x2="14.6286" y2="6.07152" stroke={line} strokeWidth="2" />
      <line x1="18.9487" y1="9.31623" x2="16.9487" y2="15.3162" stroke={line} strokeWidth="2" />
    </SvgIcon>
  );
}
